import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import { Avatar, IconButton, Drawer, List, ListItem, ListItemText, useMediaQuery, useTheme } from '@mui/material';
import { Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';
import MovdLogo from './MovdLogo.jsx';
import React, { useEffect, useState } from 'react';

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isHome = location.pathname === '/';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [account, setAccount] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('userData') || 'null');
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const refreshAccount = () => {
      try {
        setAccount(JSON.parse(localStorage.getItem('userData') || 'null'));
      } catch {
        setAccount(null);
      }
    };
    window.addEventListener('movd-auth-changed', refreshAccount);
    try {
      setAccount(JSON.parse(localStorage.getItem('userData') || 'null'));
    } catch {
      setAccount(null);
    }
    return () => window.removeEventListener('movd-auth-changed', refreshAccount);
  }, [location.pathname]);

  const isAuthenticated = Boolean(localStorage.getItem('token')) && Boolean(account);

  const signOut = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    setAccount(null);
    window.dispatchEvent(new Event('movd-auth-changed'));
    setMobileMenuOpen(false);
    navigate('/');
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const navigationItems = [
    { label: 'Home', path: '/' },
    { label: 'How It Works', path: '/how-it-works' },
    { label: 'FAQ', path: '/faq' },
    { label: 'Contact', path: '/contact' },
  ];

  return (
    <>
      <a href="#main-content" className="skip-to-content-link" tabIndex={0} style={{position:'absolute',left:0,top:0,background:'#fff',color:'#1E293B',padding:'8px 16px',zIndex:2000,transform:'translateY(-120%)',transition:'transform 0.2s',fontWeight:700}} onFocus={e=>e.target.style.transform='none'} onBlur={e=>e.target.style.transform='translateY(-120%)'}>Skip to main content</a>
      
      <AppBar 
        position="fixed" 
        color="primary" 
        sx={{ 
          background: isHome ? 'rgba(11,18,32,0.78)' : '#1E293B',
          backdropFilter: isHome ? 'blur(14px)' : 'none',
          boxShadow: 'none', 
          width: '100%',
          maxWidth: '100vw',
          overflow: 'hidden'
        }}
      >
        <Toolbar 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            minHeight: { xs: 56, sm: 64 },
            px: { xs: 2, sm: 3 },
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden'
          }}
        >
          {/* Logo */}
          <Box sx={{ flexShrink: 0 }}>
            <MovdLogo size={isMobile ? "small" : "medium"} variant="full" color="white" />
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <>
              {/* Customer navigation (center-left) */}
              <Box sx={{ display: 'flex', gap: 1, ml: 4, flexGrow: 1 }}>
                {navigationItems.map((item) => (
                  <Button
                    key={item.path}
                    color="inherit"
                    component={NavLink}
                    to={item.path}
                    sx={{
                      ...navBtnSx,
                      '&.active': {
                        color: '#93C5FD',
                        textDecoration: 'underline',
                        textUnderlineOffset: 4,
                      }
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
              
              {/* Moving partner section (right) */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexShrink: 0 }}>
                {isAuthenticated && (
                  <>
                    <Button color="inherit" component={Link} to="/customer-portal" sx={{ ...navBtnSx }}>
                      Customer portal
                    </Button>
                    <Button color="inherit" onClick={signOut} sx={{ ...navBtnSx }}>
                      Sign out
                    </Button>
                  </>
                )}
                <Button color="inherit" component={Link} to="/?portal=mover-login" sx={{ ...navBtnSx }}>
                  Mover Login
                </Button>
                <Button 
                  color="secondary" 
                  variant="contained" 
                  component={Link} 
                  to="/become-partner" 
                  sx={{ 
                    fontWeight: 700, 
                    fontSize: '11px', 
                    borderRadius: 2, 
                    ml: 1, 
                    boxShadow: 'none', 
                    textTransform: 'none',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Become a Partner
                </Button>
              </Box>
            </>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="menu"
              onClick={handleMobileMenuToggle}
              sx={{ ml: 'auto' }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer Menu */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: 280,
            bgcolor: '#1E293B',
            color: 'white',
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <MovdLogo size="small" variant="full" color="white" />
            <IconButton color="inherit" onClick={handleMobileMenuClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          <List sx={{ pt: 0 }}>
            {navigationItems.map((item) => (
              <ListItem 
                key={item.path}
                component={Link} 
                to={item.path} 
                onClick={handleMobileMenuClose}
                sx={{ 
                  color: 'inherit', 
                  textDecoration: 'none',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                  borderRadius: 1,
                  mb: 0.5
                }}
              >
                <ListItemText primary={item.label} />
              </ListItem>
            ))}
            
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              {isAuthenticated && (
                <>
                  <ListItem component={Link} to="/customer-portal" onClick={handleMobileMenuClose} sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }, borderRadius: 1, mb: 0.5 }}>
                    <ListItemText primary="Customer portal" />
                  </ListItem>
                  <ListItem component="button" onClick={signOut} sx={{ width: '100%', color: 'inherit', textAlign: 'left', background: 'none', border: 0, font: 'inherit', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }, borderRadius: 1, mb: 1 }}>
                    <ListItemText primary="Sign out" />
                  </ListItem>
                </>
              )}
              <ListItem 
                component={Link} 
                to="/?portal=mover-login" 
                onClick={handleMobileMenuClose}
                sx={{ 
                  color: 'inherit', 
                  textDecoration: 'none',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                  borderRadius: 1,
                  mb: 1
                }}
              >
                <ListItemText primary="Mover Login" />
              </ListItem>
              
              <Box sx={{ px: 2 }}>
                <Button 
                  color="secondary" 
                  variant="contained" 
                  component={Link} 
                  to="/become-partner"
                  onClick={handleMobileMenuClose}
                  fullWidth
                  sx={{ 
                    fontWeight: 700, 
                    borderRadius: 2, 
                    boxShadow: 'none', 
                    textTransform: 'none' 
                  }}
                >
                  Become a Partner
                </Button>
              </Box>
            </Box>
          </List>
        </Box>
      </Drawer>
    </>
  );
}

const navBtnSx = {
  fontWeight: 600,
  fontSize: '12px',
  borderRadius: 0,
  minWidth: 'unset',
  background: 'none',
  backgroundColor: 'transparent !important',
  boxShadow: 'none !important',
  filter: 'none !important',
  outline: 'none !important',
  color: '#fff',
  transition: 'color 0.2s',
  '&:hover': {
    color: '#2563EB',
    background: 'none',
    backgroundColor: 'transparent !important',
    textDecoration: 'underline',
    boxShadow: 'none !important',
    filter: 'none !important',
    outline: 'none !important',
  },
};
